import { useState, useEffect, useRef } from 'react';
import { apiService, ServerLogEntryDTO } from '@/services/api.service';
import styles from './LogsHistoryModal.module.scss';

interface LogsHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  serverIdentifier: string;
  serverName: string;
}

export function LogsHistoryModal({ isOpen, onClose, serverIdentifier, serverName }: LogsHistoryModalProps) {
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [logs, setLogs] = useState<ServerLogEntryDTO[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingDates, setLoadingDates] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [logFilter, setLogFilter] = useState<string>('all');
  const pageSize = 500;

  const logsContainerRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load available dates when modal opens
  useEffect(() => {
    if (isOpen && serverIdentifier) {
      loadAvailableDates();
    }
  }, [isOpen, serverIdentifier]);

  // Load logs when date changes
  useEffect(() => {
    if (selectedDate) {
      setCurrentPage(0);
      loadLogs(selectedDate, searchQuery, 0);
    }
  }, [selectedDate]);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (selectedDate) {
      searchTimeoutRef.current = setTimeout(() => {
        setCurrentPage(0);
        loadLogs(selectedDate, searchQuery, 0);
      }, 300);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const loadAvailableDates = async () => {
    setLoadingDates(true);
    setError(null);

    const result = await apiService.getServerLogsDates(serverIdentifier);

    if (result.error) {
      setError(result.error);
      setLoadingDates(false);
      return;
    }

    if (result.data) {
      setAvailableDates(result.data.dates);
      // Select today or the most recent date
      const today = new Date().toISOString().split('T')[0];
      if (today && result.data.dates.includes(today)) {
        setSelectedDate(today);
      } else if (result.data.dates.length > 0 && result.data.dates[0]) {
        setSelectedDate(result.data.dates[0]);
      }
    }

    setLoadingDates(false);
  };

  const loadLogs = async (date: string, search: string, page: number) => {
    setLoading(true);
    setError(null);

    const result = await apiService.getServerLogsHistory(
      serverIdentifier,
      date,
      search || undefined,
      page * pageSize,
      pageSize
    );

    if (result.error) {
      setError(result.error);
      setLoading(false);
      return;
    }

    if (result.data) {
      setLogs(result.data.logs);
      setTotal(result.data.total);
    }

    setLoading(false);

    // Scroll to top when loading new logs
    if (logsContainerRef.current) {
      logsContainerRef.current.scrollTop = 0;
    }
  };

  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    loadLogs(selectedDate, searchQuery, newPage);
  };

  const getLogLevelClass = (level: string) => {
    switch (level?.toUpperCase()) {
      case 'ERROR':
        return styles.logError;
      case 'WARN':
        return styles.logWarn;
      case 'DEBUG':
        return styles.logDebug;
      default:
        return styles.logInfo;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatDateLabel = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (dateStr === today.toISOString().split('T')[0]) {
      return "Aujourd'hui";
    }
    if (dateStr === yesterday.toISOString().split('T')[0]) {
      return 'Hier';
    }

    return date.toLocaleDateString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  };

  const filteredLogs = logs.filter((log) => {
    if (logFilter === 'all') return true;
    return log.level?.toUpperCase() === logFilter.toUpperCase();
  });

  const totalPages = Math.ceil(total / pageSize);

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerInfo}>
            <h2 className={styles.title}>Historique des logs</h2>
            <span className={styles.serverName}>{serverName}</span>
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Content */}
        <div className={styles.content}>
          {/* Sidebar with dates */}
          <div className={styles.sidebar}>
            <h3 className={styles.sidebarTitle}>Dates disponibles</h3>
            {loadingDates ? (
              <div className={styles.sidebarLoading}>
                <div className={styles.spinner} />
              </div>
            ) : availableDates.length === 0 ? (
              <div className={styles.noData}>
                <span className={styles.noDataIcon}>📭</span>
                <p>Aucun historique disponible</p>
              </div>
            ) : (
              <div className={styles.datesList}>
                {availableDates.map((date) => (
                  <button
                    key={date}
                    className={`${styles.dateButton} ${selectedDate === date ? styles.dateButtonActive : ''}`}
                    onClick={() => handleDateSelect(date)}
                  >
                    <span className={styles.dateLabel}>{formatDateLabel(date)}</span>
                    <span className={styles.dateValue}>{date}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Logs viewer */}
          <div className={styles.logsPanel}>
            {/* Search and filters */}
            <div className={styles.toolbar}>
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Rechercher dans les logs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <select
                className={styles.filterSelect}
                value={logFilter}
                onChange={(e) => setLogFilter(e.target.value)}
              >
                <option value="all">Tous les niveaux</option>
                <option value="INFO">INFO</option>
                <option value="WARN">WARN</option>
                <option value="ERROR">ERROR</option>
                <option value="DEBUG">DEBUG</option>
              </select>
            </div>

            {/* Logs container */}
            <div ref={logsContainerRef} className={styles.logsContainer}>
              {loading ? (
                <div className={styles.loadingState}>
                  <div className={styles.spinner} />
                  <span>Chargement des logs...</span>
                </div>
              ) : error ? (
                <div className={styles.errorState}>
                  <span className={styles.errorIcon}>⚠️</span>
                  <p>{error}</p>
                  <button className={styles.retryButton} onClick={() => loadLogs(selectedDate, searchQuery, currentPage)}>
                    Réessayer
                  </button>
                </div>
              ) : filteredLogs.length === 0 ? (
                <div className={styles.emptyState}>
                  <span className={styles.emptyIcon}>📋</span>
                  <p>Aucun log trouvé</p>
                  {searchQuery && (
                    <span className={styles.emptyHint}>
                      Essayez une recherche différente
                    </span>
                  )}
                </div>
              ) : (
                filteredLogs.map((log, index) => (
                  <div key={index} className={`${styles.logLine} ${getLogLevelClass(log.level)}`}>
                    <span className={styles.logTime}>{formatTimestamp(log.timestamp)}</span>
                    <span className={styles.logLevel}>[{log.level}]</span>
                    <span className={styles.logMessage}>{log.message}</span>
                  </div>
                ))
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  className={styles.pageButton}
                  onClick={() => handlePageChange(0)}
                  disabled={currentPage === 0 || loading}
                >
                  ««
                </button>
                <button
                  className={styles.pageButton}
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 0 || loading}
                >
                  «
                </button>
                <span className={styles.pageInfo}>
                  Page {currentPage + 1} / {totalPages}
                </span>
                <button
                  className={styles.pageButton}
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages - 1 || loading}
                >
                  »
                </button>
                <button
                  className={styles.pageButton}
                  onClick={() => handlePageChange(totalPages - 1)}
                  disabled={currentPage >= totalPages - 1 || loading}
                >
                  »»
                </button>
              </div>
            )}

            {/* Footer stats */}
            <div className={styles.footer}>
              <span className={styles.totalCount}>
                {total.toLocaleString()} logs trouvés
              </span>
              {searchQuery && (
                <span className={styles.searchInfo}>
                  Recherche : "{searchQuery}"
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
